import type fastBlur from '@vendor/fastBlur';
import addHeavyTask from '@helpers/heavyQueue';
import clearMediaElementSource from '@helpers/dom/clearMediaElementSource';
import IS_CANVAS_FILTER_SUPPORTED from '@environment/canvasFilterSupport';

const RADIUS = 2;
const ITERATIONS = 2;

let requireBlurPromise: Promise<any>;
let fastBlurFunc: typeof fastBlur;
if(!IS_CANVAS_FILTER_SUPPORTED) {
  requireBlurPromise = import('../vendor/fastBlur').then((m) => {
    fastBlurFunc = m.default;
  });
} else {
  requireBlurPromise = Promise.resolve();
}

function processBlurNext(
  img: HTMLImageElement,
  radius: number,
  iterations: number,
  canvas: HTMLCanvasElement = document.createElement('canvas'),
  maxSize?: number
) {
  let width = img.width;
  let height = img.height;
  // downscale the source before blurring (like the official clients do for the side-fill):
  // blurring a small thumbnail and upscaling it produces a far stronger, frosted-glass blur
  // than blurring the full-resolution image and scaling it down.
  if(maxSize && Math.max(width, height) > maxSize) {
    const scale = maxSize / Math.max(width, height);
    width = Math.round(width * scale) || 1;
    height = Math.round(height * scale) || 1;
  }

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', {alpha: false});
  if(IS_CANVAS_FILTER_SUPPORTED) {
    ctx.filter = `blur(${radius}px)`;
    ctx.drawImage(img, -radius * 2, -radius * 2, canvas.width + radius * 4, canvas.height + radius * 4);
  } else {
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    fastBlurFunc(ctx, 0, 0, canvas.width, canvas.height, radius, iterations);
  }

  return canvas;
}

type CacheValue = {canvas: HTMLCanvasElement, promise: Promise<void>};
const cache: Map<string, CacheValue> = new Map();
const CACHE_SIZE = 150;

export default function blur(dataUri: string, radius: number = RADIUS, iterations: number = ITERATIONS, maxSize?: number) {
  if(!dataUri) {
    throw 'no dataUri for blur: ' + dataUri;
  }

  if(cache.size > CACHE_SIZE) {
    cache.clear();
  }

  const canvas = document.createElement('canvas');
  canvas.className = 'canvas-thumbnail';

  let cached = cache.get(dataUri);
  if(!cached) {
    const img = new Image();
    const promise: CacheValue['promise'] = requireBlurPromise.then(async() => {
      // return resolve(dataUri);
      try {
        img.src = dataUri;
        await img.decode();
        await addHeavyTask({
          items: [[img, radius, iterations, canvas, maxSize]],
          context: null,
          process: processBlurNext
        }, 'unshift');
      } finally {
        clearMediaElementSource(img);
      }
    });

    cache.set(dataUri, cached = {
      canvas,
      promise
    });
    promise.catch(() => {
      if(cache.get(dataUri)?.promise === promise) {
        cache.delete(dataUri);
      }
    });
  } else {
    canvas.width = cached.canvas.width;
    canvas.height = cached.canvas.height;
    cached.promise.then(() => {
      canvas.getContext('2d').drawImage(cached.canvas, 0, 0, canvas.width, canvas.height);
    });
  }

  return {
    ...cached,
    canvas
  };
}
